import type { ReactNode } from 'react';

import styles from './AuthLayout.module.css';

/**
 * The frame around sign in and register.
 *
 * The left panel is the only executive-facing copy in the product: someone who has
 * been sent a link needs to know in one screen that this is a method platform and
 * that the agentic variant is the point. The four capabilities are named because
 * they are the four things the platform does, and a visitor who cannot see them
 * cannot tell this from a wiki.
 */

export interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
}

const CAPABILITIES: readonly { name: string; body: string }[] = [
  {
    name: 'Author',
    body: 'Define phases, stages, activities, roles and deliverables against one governed ontology.',
  },
  {
    name: 'Explore',
    body: 'Walk the method as a graph — every activity to its inputs, outputs, roles and gates.',
  },
  {
    name: 'Compare',
    body: 'Put the standard method beside its agentic reimagining, element by element.',
  },
  {
    name: 'Adopt',
    body: 'Tailor the method in a facilitated workshop and publish the result as a versioned variant.',
  },
];

export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  return (
    <div className={styles.page}>
      <aside className={styles.claim}>
        <p className={styles.brand}>
          <span className={styles.brandMark}>DPSE</span>
          <span className={styles.brandName}>Method Studio</span>
        </p>

        <div>
          <h2 className={styles.headline}>
            The delivery method as a governed graph — and its{' '}
            <em className={styles.headlineAccent}>agentic</em> reimagining beside it.
          </h2>
          <p className={styles.claimBody}>
            One ontology, four capabilities. Authored once, explored by practitioners, compared by
            executives, tailored per engagement.
          </p>
        </div>

        <dl className={styles.capabilities}>
          {CAPABILITIES.map((capability) => (
            <div className={styles.capability} key={capability.name}>
              <dt className={styles.capabilityName}>{capability.name}</dt>
              <dd className={styles.capabilityBody}>{capability.body}</dd>
            </div>
          ))}
        </dl>
      </aside>

      <main className={styles.panel}>
        <div className={styles.form}>
          <div>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
